import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  LayoutDashboard, Bot, Phone, CreditCard, Settings,
  LogOut, ShieldCheck, Menu, X, Mic, ChevronDown, MailWarning
} from 'lucide-react';

export default function Layout() {
  const { user, logout, emailVerifyDeadline, resendVerification } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [resending, setResending] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Колонка BOOLEAN приходит как true/false; сравниваем с false явно, чтобы баннер не
  // мигал у пользователя, чей профиль ещё не подгрузился (undefined).
  const emailUnconfirmed = user?.email_verified === false;
  const hoursLeft = emailVerifyDeadline
    ? Math.max(0, Math.ceil((emailVerifyDeadline * 1000 - Date.now()) / 3600000))
    : null;

  const handleResend = async () => {
    setResending(true);
    try {
      await resendVerification();
      toast.success('Confirmation email sent — check your inbox.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not send the confirmation email');
    } finally {
      setResending(false);
    }
  };

  const navItems = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/calls', label: 'Call History', icon: Phone },
    { to: '/billing', label: 'Billing', icon: CreditCard },
    { to: '/account', label: 'Account', icon: Settings },
  ];

  if (user?.role === 'partner' || user?.role === 'admin') {
    navItems.push({ to: '/partner', label: 'Partner', icon: ShieldCheck });
  }
  if (user?.role === 'admin') {
    navItems.push({ to: '/admin', label: 'Admin Panel', icon: ShieldCheck });
  }

  return (
    <div className="layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar--open' : ''}`}>
        <div className="sidebar__logo">
          <Mic size={24} className="logo-icon" />
          <span>Voice AI Portal</span>
          <button className="sidebar-close" onClick={() => setSidebarOpen(false)}>
            <X size={20} />
          </button>
        </div>

        {/* Demo badge */}
        {user?.is_demo === 1 && (
          <div className="demo-badge">
            <span>🎯 Demo Mode</span>
            <small>${user?.balance?.toFixed(2)} remaining</small>
          </div>
        )}

        <nav className="sidebar__nav">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => `nav-item ${isActive ? 'nav-item--active' : ''}`}
              onClick={() => setSidebarOpen(false)}
            >
              <Icon size={18} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar__footer">
          <div className="user-info">
            <div className="user-avatar">
              {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="user-details">
              <span className="user-name">{user?.full_name}</span>
              <span className="user-plan">{user?.plan?.toUpperCase()}</span>
            </div>
          </div>
          <button className="btn-logout" onClick={handleLogout} title="Logout">
            <LogOut size={18} />
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="main-wrapper">
        <header className="topbar">
          <button className="menu-toggle" onClick={() => setSidebarOpen(true)}>
            <Menu size={22} />
          </button>
          <h1 className="topbar__title">Voice AI Portal</h1>
          <div className="topbar__right">
            <span className="balance-chip">
              💰 ${user?.balance?.toFixed(2) || '0.00'}
            </span>
          </div>
        </header>

        <main className="main-content">
          {emailUnconfirmed && (
            <div className="banner banner--warning" style={{ marginBottom: 16 }}>
              <MailWarning size={18} />
              <span>
                Confirm your email address <strong>{user?.email}</strong> to keep this account
                active{hoursLeft !== null && ` — ${hoursLeft}h left`}. Check your inbox for the
                confirmation link.
              </span>
              <button
                className="btn btn-sm btn-ghost"
                onClick={handleResend}
                disabled={resending}
                style={{ marginLeft: 'auto' }}
              >
                {resending ? 'Sending...' : 'Resend email'}
              </button>
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
